import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  useTheme,
} from "@mui/material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { tokens } from "../../../../theme";
import CustomTextField from "../../../../components/common/Textfield";
import apiService from "../../../../service/Api";
import { Venue } from "../../../../types/venue";

interface AddVenueDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (venue: Venue) => void;
}

type VenueForm = Omit<Venue, "id">;
const EMPTY: VenueForm = {
  name: "",
  address_one: "",
  address_two: "",
  suburb: "",
  city: "",
};

const AddVenueDialog: React.FC<AddVenueDialogProps> = ({
  open,
  onClose,
  onSaved,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);
  const isDarkMode = theme.palette.mode === "dark";
  const color400 = colors.primary[400] || "#724E41";
  const color10 = colors.primary[10] || "#C29363";
  const cities: string[] = themeState.cities || [];

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<VenueForm>(EMPTY);

  const set = (field: keyof VenueForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleClose = () => {
    setForm(EMPTY);
    onClose();
  };

  const isValid =
    !!form.name.trim() && !!form.address_one.trim() && !!form.city;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const res = await apiService.post<Venue>("/api/venues/venue/", form);
      toast.success("Venue added successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      onSaved(res.data);
      handleClose();
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(detail || "Failed to add venue.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>Add New Venue</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <CustomTextField
            label="Venue Name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            required
            autoFocus
          />
          <CustomTextField
            label="Address One"
            value={form.address_one}
            onChange={(e) => set("address_one", e.target.value)}
            required
          />
          <CustomTextField
            label="Address Two"
            value={form.address_two}
            onChange={(e) => set("address_two", e.target.value)}
          />
          <CustomTextField
            label="Suburb"
            value={form.suburb}
            onChange={(e) => set("suburb", e.target.value)}
          />
          <CustomTextField
            select
            label="City"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            required
          >
            <MenuItem value="">
              <em>Select city</em>
            </MenuItem>
            {cities.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </CustomTextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          sx={{ color: isDarkMode ? colors.primary[200] : "#4A5568" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !isValid}
          sx={{
            background: `linear-gradient(135deg, ${color400} 0%, ${color10} 100%)`,
            color: "#fff",
            fontWeight: 600,
            textTransform: "none",
            borderRadius: "8px",
            px: 3,
            boxShadow: "none",
            "&:hover": { opacity: 0.9, boxShadow: "none" },
          }}
        >
          {saving ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Save Venue"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddVenueDialog;
